import { CheckIcon, ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import type { SetupStep } from './types'
import { ago } from './types'

/** The five setup steps as a thin bar — one segment per step. */
export function StepBar({ steps }: { steps: SetupStep[] }) {
  return (
    <div className="flex gap-1" aria-hidden="true">
      {steps.map((s) => (
        <i
          key={s.key}
          className={`block h-1.5 w-6 rounded-full ${
            s.status === 'done' ? 'bg-emerald-500' : s.status === 'failed' ? 'bg-red-500' : s.status === 'running' ? 'bg-blue-500 animate-pulse' : 'bg-slate-200'
          }`}
        />
      ))}
    </div>
  )
}

/** Where setup is now, in words. */
export function stepSummary(steps: SetupStep[]): { text: string; tone: 'ok' | 'bad' | 'run' | 'wait' } {
  const failed = steps.find((s) => s.status === 'failed')
  if (failed) return { text: `Stopped at ${failed.label}${failed.error ? `: ${failed.error}` : ''}`, tone: 'bad' }
  const running = steps.find((s) => s.status === 'running')
  if (running) return { text: `${running.label}…`, tone: 'run' }
  if (steps.every((s) => s.status === 'done')) return { text: 'Ready', tone: 'ok' }
  const next = steps.find((s) => s.status === 'waiting')
  return { text: next ? `Waiting to start: ${next.label}` : 'Waiting', tone: 'wait' }
}

export default function Timeline({ steps, onRetry, retrying }: { steps: SetupStep[]; onRetry?: (restart: boolean) => void; retrying?: boolean }) {
  const failed = steps.some((s) => s.status === 'failed')
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
      <ol>
        {steps.map((s) => (
          <li key={s.key} className={`grid grid-cols-[24px_1fr_auto] gap-3 items-center px-3.5 py-2.5 border-b border-slate-100 last:border-b-0 text-sm ${s.status === 'failed' ? 'bg-red-50' : ''}`}>
            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center ${
                s.status === 'done' ? 'bg-emerald-50 text-emerald-600' : s.status === 'failed' ? 'bg-red-600 text-white' : s.status === 'running' ? 'bg-blue-50 text-blue-600' : 'border border-slate-300 text-slate-400'
              }`}
            >
              {s.status === 'done' ? <CheckIcon className="w-3.5 h-3.5" /> : s.status === 'failed' ? <ExclamationTriangleIcon className="w-3.5 h-3.5" /> : s.status === 'running' ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : null}
            </span>
            <span className="min-w-0">
              <b className="font-semibold text-slate-900">{s.label}</b>
              {s.error && <span className="block text-xs text-red-700 mt-0.5 break-words">{s.error}</span>}
            </span>
            <span className="text-xs text-slate-500 whitespace-nowrap">{s.status === 'waiting' ? '' : ago(s.at)}</span>
          </li>
        ))}
      </ol>
      {onRetry && failed && (
        <div className="flex flex-wrap gap-2 justify-end px-3.5 py-2.5 border-t border-slate-100 bg-slate-50">
          <button type="button" disabled={retrying} onClick={() => onRetry(true)} className="px-3 h-8 text-xs font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50">Start over from the top</button>
          <button type="button" disabled={retrying} onClick={() => onRetry(false)} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            <ArrowPathIcon className="w-3.5 h-3.5" />Retry from here
          </button>
        </div>
      )}
    </div>
  )
}
